import React from "react";

import { Button, Ref } from "semantic-ui-react";
import eventStack from "semantic-ui-react/dist/commonjs/lib/eventStack";

interface TriggerProps {
	onFocus: (e: React.FocusEvent, ...rest: unknown[]) => void;
	onBlur: (e: React.FocusEvent, ...rest: unknown[]) => void;
	onClick: (e: React.MouseEvent, ...rest: unknown[]) => void;
	className?: string;
}

interface Trigger {
	extraNode?: HTMLElement | null;
	key: string | number | null;
	type: string;
	props: TriggerProps;
}

interface ShowMoreProps {
	limit: number;
	eventPool: string;
	hideOnEscape: boolean;
	showOnTriggerClick: boolean;
	hideOnTriggerClick: boolean;
	hideOnTriggerBlur: boolean;
	showOnTriggerFocus: boolean;
	showOnTriggerMouseEnter: boolean;
	hideOnTriggerMouseLeave: boolean;
	mouseEnterDelay: number;
	mouseLeaveDelay: number;
	hideOnDocumentClick: boolean;
	trigger: Trigger;
	children: React.ReactElement[];
	triggerForHide?: Trigger;
	className?: string;
}

interface ShowMoreState {
	show: boolean;
}

export default class ShowMore extends React.Component<ShowMoreProps, ShowMoreState> {
	state: ShowMoreState = { show: false };

	private extraNode: HTMLElement | null = null;
	private triggerNode: HTMLElement | null = null;

	mouseEnterTimer = 0;
	mouseLeaveTimer = 0;

	static defaultProps: Partial<ShowMoreProps> = {
		limit: 5,
		eventPool: "default",
		hideOnEscape: true,
		showOnTriggerClick: true,
		hideOnTriggerClick: true,
		hideOnTriggerBlur: false,
		showOnTriggerFocus: false,
		showOnTriggerMouseEnter: false,
		hideOnTriggerMouseLeave: true,
		mouseEnterDelay: 500,
		mouseLeaveDelay: 1000,
		hideOnDocumentClick: true,
		trigger: <Button basic size="mini" icon="ellipsis horizontal" />,
	};

	onShow = () => this.setState({ show: true });
	onHide = () => this.setState({ show: false });

	handleDocumentClick = (e: React.MouseEvent) => {
		if (e.target instanceof Node) {
			if (this.triggerNode && this.triggerNode.contains(e.target)) return;
			if (this.extraNode && this.extraNode.contains(e.target)) return;
		}

		if (this.props.hideOnDocumentClick) this.onHide();
	};

	handleEscape = (e: React.KeyboardEvent) => {
		if (this.props.hideOnEscape && e.keyCode == 27) this.onHide();
	};

	handleTriggerBlur = (e: React.FocusEvent, ...rest: unknown[]) => {
		const { trigger, hideOnTriggerBlur } = this.props;

		// Call original event handler
		if (trigger.props.onBlur) trigger.props.onBlur(e, ...rest);

		// Do not hide if focus is given to the transition part
		let didFocusTransition = false;
		if (e.relatedTarget instanceof Node && trigger.extraNode && trigger.extraNode.contains(e.relatedTarget)) {
			didFocusTransition = true;
		}

		if (hideOnTriggerBlur && !didFocusTransition) this.onHide();
	};

	handleTriggerClick = (e: React.MouseEvent, ...rest: unknown[]) => {
		const { trigger, hideOnTriggerClick, showOnTriggerClick } = this.props;

		// Call original event handler
		if (trigger.props.onClick) trigger.props.onClick(e, ...rest);

		if (this.state.show && hideOnTriggerClick) {
			this.onHide();
		} else if (!this.state.show && showOnTriggerClick) {
			this.onShow();
		}
	};

	handleTriggerFocus = (e: React.FocusEvent, ...rest: unknown[]) => {
		const { trigger, showOnTriggerFocus } = this.props;

		if (trigger.props.onFocus) trigger.props.onFocus(e, ...rest);

		if (showOnTriggerFocus) this.onShow();
	};

	handleMouseLeave = () => {
		window.clearTimeout(this.mouseEnterTimer);
		if (this.props.hideOnTriggerMouseLeave) {
			this.mouseLeaveTimer = window.setTimeout(this.onHide, this.props.mouseLeaveDelay);
		}
	};

	handleMouseEnter = () => {
		window.clearTimeout(this.mouseLeaveTimer);
		if (this.props.showOnTriggerMouseEnter) {
			this.mouseEnterTimer = window.setTimeout(this.onShow, this.props.mouseEnterDelay);
		}
	};

	componentDidMount(): void {
		eventStack.sub("keydown", this.handleEscape, { pool: this.props.eventPool });
		eventStack.sub("click", this.handleDocumentClick, { pool: this.props.eventPool });
	}

	componentWillUnmount(): void {
		eventStack.unsub("keydown", this.handleEscape, { pool: this.props.eventPool });
		eventStack.unsub("click", this.handleDocumentClick, { pool: this.props.eventPool });
	}

	handleExtraNode = (node: HTMLElement): void => {
		this.extraNode = node;
	};

	handleTriggerNode = (node: HTMLElement): void => {
		this.triggerNode = node;
	};

	render() {
		const allChildren = this.props.children;

		let limit = this.props.limit;
		if (!limit) limit = allChildren.length;

		const initialChildren = allChildren.slice(0, limit);
		const hiddenChildren = allChildren.slice(limit);
		const triggerForHide = this.props.triggerForHide || this.props.trigger;

		return (
			<>
				{initialChildren}
				{hiddenChildren.length && (
					<span onMouseEnter={this.handleMouseEnter} onMouseLeave={this.handleMouseLeave}>
						<Ref innerRef={this.handleExtraNode}>
							<span>{this.state.show && hiddenChildren}</span>
						</Ref>
						<Ref innerRef={this.handleTriggerNode}>
							{this.state.show
								? triggerForHide &&
									React.cloneElement(triggerForHide, {
										onBlur: this.handleTriggerBlur,
										onClick: this.handleTriggerClick,
										onFocus: this.handleTriggerFocus,
										className: `${this.props.className} ${this.state.show ? "active" : ""}`,
									})
								: this.props.trigger &&
									React.cloneElement(this.props.trigger, {
										onBlur: this.handleTriggerBlur,
										onClick: this.handleTriggerClick,
										onFocus: this.handleTriggerFocus,
										className: `${this.props.className} ${this.state.show ? "active" : ""}`,
									})}
						</Ref>
					</span>
				)}
			</>
		);
	}
}
